import moment from "moment";
import { Fragment, useEffect, useState } from "react";
import { useAsync } from "react-async";
import BillingType from "../../api/models/billingType";
import TaskType from "../../api/models/tasktype";
import { getBillingTypes } from "../../api/services/billingService";
import { getTaskTypes } from "../../api/services/taskTypeService";
import { BillToLookup, EntryTypes, TaskTypeLookup } from '../../common/constants';
import { daysOfWeek, getBillingTypeName, isEntryValid } from "../../common/utils";
import { validateEntryMinutesForDay, validateOnTaskTypeChange } from "./shared";
import DayEntry from "./types/dayEntry";
import TimesheetTaskEntries from './types/timesheetTaskEntries';
import Step2Props from "./types/step2.child";
import WeekItem from "../../api/models/timesheetWeekItem";
import TimesheetWeekTask from "../../api/models/TimesheetWeekTask";

let taskTypesCache: TaskType[] = [];
const loadTaskTypes = async ({ disciplineId }: any) => {
    if (typeof taskTypesCache !== "undefined" && taskTypesCache.length > 0)
        return taskTypesCache;

    taskTypesCache = (await (await getTaskTypes(disciplineId, undefined, EntryTypes.NonStudent)).data?.taskTypes?.filter(t => t.isVisible)) ?? [];
    return taskTypesCache;
}

let billingTypes: BillingType[] = [];
const loadBillingTypes = async () => {
    if (typeof billingTypes !== "undefined" && billingTypes.length > 0)
        return billingTypes;

    billingTypes = (await (await getBillingTypes()).data) ?? [];
    return billingTypes
}

/**
 * Entry options for non students
 */
const EntryForNonStudent = ({
    entryState,
    setEntryState,
    editNote,
    saveEntry,
    setHasChanges,
    hasChanges,
    setIsSaved,
    isSaved,
    selectedDistrictProgramOption
}: Step2Props) => {
    //selection
    const [taskTypeId, setTaskTypeId] = useState<number>(entryState.taskTypeId ?? -1);
    const [billToId, setBillToId] = useState<number>(entryState.billToId ?? -1);
    //options
    const [taskTypes, setTaskTypes] = useState<TaskType[]>((entryState.taskTypes ?? []).sort((a, b) => a.name.localeCompare(b.name)).sort(x => x.hasRecoupment ? -1 : 1));
    //validation
    const [validationMsg, setValidationMsg] = useState<string | null | JSX.Element>();
    const [timesheetEntryTasks, setTimesheetEntryTasks] = useState<TimesheetTaskEntries[]>(entryState.timesheetEntryTasks ?? []);
    const [isLoaded, setIsLoaded] = useState<boolean>(false);
    const [schoolIsEmpty, setSchoolIsEmpty] = useState<boolean>(!entryState.schoolId || entryState.schoolId < 0);
    /**
     * Load the task types for this entry on load (promiseFn)
     */
    useAsync({
        promiseFn: loadTaskTypes,
        disciplineId: entryState.disciplineId,
        onResolve: (res) => {
            const loadedTaskTypes = (res.filter(t => t.isVisible) ?? []).sort((a, b) => a.name.localeCompare(b.name)).sort(x => x.hasRecoupment ? -1 : 1).sort((a, b) => a.name.localeCompare(b.name));
            setTaskTypes(loadedTaskTypes);
            // Also update the entry state task types
            entryState.taskTypes = loadedTaskTypes;
        }
    });

    /**
     * On page load, this effect will go through and add the timesheet
     * entry tasks for which we already have logged data
     */
    useEffect(() => {
        setSchoolIsEmpty(!entryState.schoolId || entryState.schoolId < 0);
        if (entryState.timesheetEntryTasks?.length === 0) {
            populateWithSelectedTimesheet();
        } else {
            populateWithTimesheetEntries();
        }
    }, []);

    const populateWithTimesheetEntries = () => {
        //If we don't have a selected timesheet or the timesheet items are empty, exit early
        if (!entryState.timesheetEntryTasks)
            return;

        //Otherwise get our entry tasks ready
        const entryTasks: TimesheetTaskEntries[] = [];

        //Loop through the timesheet items, but only the ones that have the specified task types
        entryState.timesheetEntryTasks.filter(x =>
            x.taskTypeId === TaskTypeLookup.ClinicalSupervision ||
            x.taskTypeId === TaskTypeLookup.Mtss ||
            x.taskTypeId === TaskTypeLookup.Other
        ).forEach(task => {
            //Set up the entries for this task
            const tempEntries: DayEntry[] = [];
            daysOfWeek(entryState.startDate).forEach((day) => {
                tempEntries.push({
                    billToId: task.taskTypeId === TaskTypeLookup.Other ? (!task.billToId ? BillToLookup.School : task.billToId) : BillToLookup.School,
                    dateOfEntry: day,
                    notes: task.entries.find(y => moment(y.date ?? y.dateOfEntry)?.isSame(day))?.notes ?? '',
                    minutes: task.entries.find(y => moment(y.date ?? y.dateOfEntry)?.isSame(day))?.minutes,
                    enabled: true
                });
            });

            //Push the entry to the array
            entryTasks.push({
                startDate: entryState.startDate,
                schoolId: entryState.schoolId,
                endDate: entryState.endDate,
                taskType: taskTypes.find(y => y.taskTypeId === taskTypeId),
                taskTypeId: task.taskTypeId,
                billToId: task.taskTypeId === TaskTypeLookup.Other ? (!task.billToId ? BillToLookup.School : task.billToId) : BillToLookup.School,
                entries: tempEntries
            });
        });

        //Set our hook
        setTimesheetEntryTasks(entryTasks);

        //Make sure we know the page has loaded so we don't run it again after saving a note ~PTSU-961
        setIsLoaded(true);
    }

    const populateWithSelectedTimesheet = () => {
        //If we don't have a selected timesheet or the timesheet items are empty, exit early
        if (!entryState.selectedTimesheet || (!entryState.selectedTimesheet.items && timesheetEntryTasks.length === 0))
            return;

        //Otherwise get our entry tasks ready
        const entryTasks: TimesheetTaskEntries[] = [];
        //Loop through the timesheet items, but only the ones that have the specified task types
        entryState.selectedTimesheet.items.forEach(item => {
            item.tasks.filter(x =>
                (((x.taskTypeId === TaskTypeLookup.ClinicalSupervision || x.taskTypeId === TaskTypeLookup.Mtss) && x.schoolId == entryState.schoolId)
                    || (x.taskTypeId === TaskTypeLookup.Other && x.schoolId === (x.entries.some(e => e.billToId == BillToLookup.District) ? null : entryState.schoolId) && item.districtId === entryState.districtId))
                && x.programId === (entryState.programId && entryState.programId > 0 ? entryState.programId : null)
            ).forEach(task => {
                //Set up the entries for this task
                const tempEntries: DayEntry[] = [];
                daysOfWeek(entryState.startDate).forEach((day) => {
                    tempEntries.push({
                        billToId: task.taskTypeId === TaskTypeLookup.Other ? (!item.billToId ? BillToLookup.School : item.billToId) : BillToLookup.School,
                        dateOfEntry: day,
                        notes: task.entries.find(y => moment(y.date ?? y.dateOfEntry)?.isSame(day))?.notes ?? '',
                        minutes: task.entries.find(y => moment(y.date ?? y.dateOfEntry)?.isSame(day))?.minutes,
                        enabled: true
                    });
                });

                //Push the entry to the array
                entryTasks.push({
                    startDate: entryState.startDate,
                    endDate: entryState.endDate,
                    schoolId: entryState.schoolId,
                    taskType: taskTypes.find(y => y.taskTypeId === taskTypeId),
                    taskTypeId: task.taskTypeId,
                    billToId: task.taskTypeId === TaskTypeLookup.Other ? (!item.billToId ? BillToLookup.School : item.billToId) : item.billToId ?? undefined,
                    entries: tempEntries
                });
            });
        });

        //Set our hook
        setTimesheetEntryTasks(entryTasks);

        //Make sure we know the page has loaded so we don't run it again after saving a note ~PTSU-961
        setIsLoaded(true);
    }

    /**
     * Check if we can enter time
     */
    const canEnterTime = () => {
        if (billToId === BillToLookup.District && taskTypeId === TaskTypeLookup.Other && entryState.allowsDistrictTime === false)
            return false;

        if ((entryState.schoolId === undefined || entryState.schoolId === -1) && entryState.allowsDistrictTime === false)
            return false;

        return true;
    }

    /**
     * When the parent component save function is finished, this effect will run
     */
    useEffect(() => {
        if (isSaved) {
            resetEntries();
            setIsSaved(false);
        }
    }, [isSaved]);

    /**
     * Resets the entries
     */
    const resetEntries = () => {
        //Reset entries
        entryState.timesheetEntryTasks = [];
        setTimesheetEntryTasks([]);
        setEntryState(entryState);
    }

    /**
     * Saves pending changes if there are any.
     *
     * @return {Promise<void>} Returns a promise that resolves when the pending changes are saved.
     */
    const savePendingChanges = async () => {
        //if we have pending changes, save them.
        if (hasChanges) {
            try {
                const didSave = await saveEntry();
                if (didSave) {
                    setHasChanges(false);
                }
            } catch (error: any) {
                window.alert(typeof error === 'string' ? error : 'An error occurred while saving your entry. Please try again.');
            }
        }
    }

    /**
     * Occurs when the user selects a task type from the dropdown
     * @param taskTypeId 
     */
    const taskTypeChanged = async (taskTypeId: number, billToId?: number) => {
        //Save any pending changes
        //savePendingChanges();

        setTaskTypeId(taskTypeId);
        entryState.taskTypeId = taskTypeId;

        //only other allows billing to district
        if (billToId) billToChanged(billToId, taskTypeId, true)

        let newTaskEntry: TimesheetTaskEntries | undefined = undefined;

         let updatedTasks: TimesheetTaskEntries[] = [];

        //Toggle the selected task type
        setTimesheetEntryTasks(current => {
            //Find the entry task
            const entryTask = current.find(x => x.taskTypeId === taskTypeId && x.billToId === billToId);
            //If the entry task exists, then remove it from the array
            // let updatedTasks: TimesheetTaskEntries[] = [];

            if (entryTask) {
                updatedTasks = current.filter(x => !(x.taskTypeId === taskTypeId && x.billToId === billToId));
            }
            else {

                //Otherwise, generate the blank entries and add the task type to the array
                const tempEntries: DayEntry[] = [];
                daysOfWeek(entryState.startDate).forEach((day) => {
                    tempEntries.push({
                        dateOfEntry: day,
                        notes: "",
                        minutes: taskTypeId === TaskTypeLookup.Absence || taskTypeId === TaskTypeLookup.Unavailable ? undefined : 0,
                        billToId: billToId,
                        enabled: true//day.isSameOrAfter(student?.startDate) && (student?.endDate === null || day.isSameOrBefore(student?.endDate)),
                    });
                });

                newTaskEntry = {
                    startDate: entryState.startDate,
                    endDate: entryState.endDate,
                    taskType: taskTypes.find(y => y.taskTypeId === taskTypeId),
                    taskTypeId,
                    billToId,
                    isRecoupment: undefined,
                    schoolId: entryState.schoolId,
                    isMakeupTime: undefined,
                    entries: tempEntries
                };
                addTimesheetItem(newTaskEntry);

                updatedTasks = [...current, newTaskEntry];
            }


            return updatedTasks;
        });


       await syncExistingEntries();

       setValidationMsg(validateOnTaskTypeChange(updatedTasks, entryState));


    }

    /**
     * If the selected timesheet does not have the item in question, add the item
     * @param entryTask 
     * @returns 
     */
    const addTimesheetItem = (entryTask: TimesheetTaskEntries | undefined) => {
        //If no entry task was passed, exit early and do nothing
        if (!entryTask) return;

        //Check to see if the task exists in the timesheet
        const doesExist = entryState.selectedTimesheet?.items?.some(x =>
            x.billToId === entryTask.billToId ||
            x?.tasks.some(y => y.taskTypeId === entryTask.taskTypeId
            ));

        //Return early if the task does exist
        if (doesExist) return;

        //Get the billTo name
        const billTo = billingTypes.find(x => x.billingTypeId === entryTask.billToId);

        //Create the task
        const tasks: TimesheetWeekTask[] = [{
            taskTypeName: entryTask.taskType?.taskTypeName ?? '',
            taskTypeId: entryTask.taskTypeId ?? 0,
            entryTypeName: entryState.entryTypeName ?? '',
            entryTypeId: entryState.entryTypeId ?? 0,
            programName: entryState.programName ?? '',
            programId: entryState.programId,
            schoolId: entryState.schoolId ?? 0,
            schoolName: entryState.school?.name ?? '',
            entries: entryTask.entries
        }];

        //Create the item
        const tmpItem: WeekItem = {
            title: `${billTo?.name ?? 'School'} (Non-Student)`,
            subTitle: entryState.schoolName ?? '',
            description: entryState.studentName ?? '',
            districtId: entryState.districtId ?? 0,
            schoolId: entryState.schoolId ?? 0,
            students: [],
            billToId: entryTask.billToId,
            tasks: tasks,
            day: entryState.startDate,
        };

        //Make sure the items array is actually an array
        if (entryState.selectedTimesheet && !entryState.selectedTimesheet.items) {
            entryState.selectedTimesheet.items = [];
        }

        //Add the temp item
        entryState.selectedTimesheet?.items?.push(tmpItem);

        //toggle(undefined, true);
        setEntryState(entryState);
    }

    /**
     * Occurs when the user selects a bill to from the dropdown
     * @param billToId 
     * @param ignoreSave 
     */
    const billToChanged = async (billToId: number, taskTypeId?: number, ignoreSave: boolean = false) => {
        //Save any pending changes
        //if (!ignoreSave) savePendingChanges();

        setBillToId(billToId);
        entryState.billToId = billToId;

        //check for existing entries
        syncExistingEntries();
    }

    /**
     * Updates the minutes when a time entry is added/changed
     * @param index 
     * @param inputMinutes 
     * @param taskEntries 
     */
    const updateEntryTime = (index: number, inputMinutes: number, taskTypeId: number, billToId?: number) => {
        //Get the entry task
        const entryTaskSearch = timesheetEntryTasks.find(x => x.taskTypeId === taskTypeId && (!billToId || (taskTypeId === TaskTypeLookup.Other ? x.billToId === billToId : true)));

        const entryTask = Array.isArray(entryTaskSearch) ? entryTaskSearch[0] : entryTaskSearch;

        //If none was found, return early
        if (!entryTask) {
            return;
        }

        const isValid = isEntryValid(timesheetEntryTasks, index, inputMinutes);

        //if there is a validation error users can't add more time to other entries, but they are allowed to reduce, otherwise they can't change anything
        if (entryState.hasValidationErrors && !isValid) {
            alert("You have validation errors, please fix those issues before adjusting other entries");
            return;
        }


        //Loop through the entry tasks and find the one we are updating
        const entryTasks = [...timesheetEntryTasks];
        entryTasks.map(x => {
            if (x.taskTypeId === taskTypeId && (!billToId || (taskTypeId === TaskTypeLookup.Other ? x.billToId === billToId : true))) {
                if (x.entries[index] !== undefined) {
                    x.entries[index].minutes = inputMinutes;
                }
                return x;
            }

            return x;
        });

        //Update our hook and entryState with the updated tasks
        setTimesheetEntryTasks(entryTasks);
        entryState.timesheetEntryTasks = entryTasks;

        //Set our entry state
        setEntryState(entryState);
        //check's validation, set's the validation message to the result of the validation
        setValidationMsg(validateEntryMinutesForDay(entryState, entryTask.entries, inputMinutes));

        setHasChanges(true);
    }

    /**
     * Displays the notes when the edit link is clicked
     * @param index number
     * @param taskTypeId number
     */
    const editNoteClicked = (index: number, taskTypeId?: number, billToId?: number) => {
        //Verify we have a task type before continuing
        if (!taskTypeId) return;

        //Get entries
        const searchEntries = (timesheetEntryTasks.find(x => x.taskTypeId === taskTypeId && x.billToId === billToId)?.entries as unknown) as DayEntry[] ?? [];
        //Reset the state of the validtion errors, they will be checked again with this component loads
        // entryState.hasValidationErrors = false;
        // setValidationMsg(null);
        setEntryState(entryState => {
            const tmpEntryState = { ...entryState };
            tmpEntryState.taskTypeId = taskTypeId
            return tmpEntryState;
        });

        //Load the edit note dialog
        editNote({
            dayEntry: searchEntries![index],
            entryModel: entryState,
            taskTypeId,
            billToId
        });
        setHasChanges(true);
    }

    /**
     * Check for existing entries that matches the options selected, no duplicated entries! 
     * @param tempEntries 
     * @returns 
     */
    const syncExistingEntries = () => {
        setTimesheetEntryTasks(current => {
            //Loop through each entry and find it's corresponding value
            current.map(entryTask => {
                if (entryTask.entries.some(x => x.minutes !== undefined && x.minutes > 0)) {
                    return;
                }

                //clear old values
                entryTask.entries.map((entry) => {
                    entry.notes = "";
                    entry.minutes = undefined;
                    return entry;
                });

                //make sure we have a selected timesheet
                if (typeof entryState.selectedTimesheet === "undefined")
                    return;

                //if bill to is set to district then school should be null
                var schoolId = (entryTask.billToId === BillToLookup.District) ? null : entryState.schoolId;

                entryTask.entries.map(entry => {
                    if (entryState.selectedTimesheet) {
                        var item = entryState.selectedTimesheet.items?.find(i =>
                            i.billToId === entry.billToId &&
                            i.districtId === entryState.districtId &&
                            i.schoolId === schoolId);

                        var task = item?.tasks?.find(t =>
                            t.entryTypeId === entryState.entryTypeId &&
                            t.taskTypeId === entryTask.taskTypeId &&
                            t.schoolId === schoolId &&
                            t.programId === (((entryState.programId ?? 0) <= 0) ? null : entryState.programId));

                        //look for matching entries and update the temp entries to match them, users can't create duplicate ones! 
                        if (typeof task !== "undefined") {
                            var date = moment(entry.dateOfEntry ?? entry.date);
                            var tempEntry = task.entries.find(e => date.isSame(e.dateOfEntry ?? e.date, 'day'));

                            if (tempEntry) {
                                entry.notes = tempEntry.notes?.valueOf();
                                entry.minutes = tempEntry.minutes;
                                entry.timesheetEntryId = tempEntry.entryId?.valueOf();
                            }
                        }
                    }

                    return entry;
                });
            });
            return current;
        });
        setEntryState(entryState => {
            const tmpEntryState = { ...entryState };
            tmpEntryState.timesheetEntryTasks = timesheetEntryTasks;
            return tmpEntryState;
        });
    }

    /**
     * This will revalidate each entry after the user edit notes
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        setValidationMsg(null);
        timesheetEntryTasks.map(entryTask => {
            const entries = entryTask.entries;

            for (var i = 0; i < entries.length; i++) {
                if (entries[i].minutes !== undefined) {
                    var msg = validateEntryMinutesForDay(entryState, entries, entries[i].minutes!);
                    if (msg != null) {
                        setValidationMsg(msg);
                        break;
                    }
                }
            }
        });
    }, []);

    /**
     * Render function
     */
    return (
        <Fragment>
            {canEnterTime() === true && (
                <Fragment>
                    <div className="task-type-boxes">
                        {/** Task Type */}
                        <label>Task Type</label>
                        {taskTypes
                            ?.filter(x => x.taskTypeId !== TaskTypeLookup.Other)
                            ?.sort((a, b) => a.name.localeCompare(b.name))
                            ?.sort(x => x.hasRecoupment ? -1 : 1)
                            ?.map((taskType, i) => <>
                                <label className={`w-full inline-block`} style={{ color: schoolIsEmpty ? 'gray' : '' }} htmlFor={`task-option-${taskType.taskTypeId}`} key={`task-option-label-${taskType.taskTypeId}`}>
                                    <input
                                        id={`task-option-${taskType.taskTypeId}`}
                                        name={`task-option-${taskType.taskTypeId}`}
                                        key={`task-option-checkbox-${taskType.taskTypeId}`}
                                        type="checkbox" value={BillToLookup.School}
                                        checked={timesheetEntryTasks.filter(x => x.taskTypeId === taskType.taskTypeId).length > 0}
                                        onChange={(e) => taskTypeChanged(taskType.taskTypeId!, Number(e.target.value))}
                                        disabled={schoolIsEmpty}
                                    /> {taskType.name}
                                </label>
                            </>)}

                        {/** TODO: May want to iterate through BillToLookup to make this dynamic */}
                        {/** Other - School */}
                        <label className={`w-full inline-block`} style={{ color: schoolIsEmpty ? 'gray' : '' }} htmlFor={`bill-option-${BillToLookup.School}`} key={`bill-option-label-${BillToLookup.School}`}>
                            <input
                                id={`bill-option-${BillToLookup.School}`}
                                name={`bill-option-${BillToLookup.School}`}
                                key={`bill-option-checkbox-${BillToLookup.School}`}
                                type="checkbox" value={BillToLookup.School}
                                checked={timesheetEntryTasks.filter(x => x.taskTypeId === TaskTypeLookup.Other && x.billToId === BillToLookup.School).length > 0}
                                onChange={(e) => taskTypeChanged(TaskTypeLookup.Other, Number(e.target.value))}
                                disabled={schoolIsEmpty}
                            /> {'Other Billed To School'}
                        </label>

                        {/** Other - District */}
                        <label className={`w-full inline-block`} htmlFor={`bill-option-${BillToLookup.District}`} key={`bill-option-label-${BillToLookup.District}`}>
                            <input
                                id={`bill-option-${BillToLookup.District}`}
                                name={`bill-option-${BillToLookup.District}`}
                                key={`bill-option-checkbox-${BillToLookup.District}`}
                                type="checkbox" value={BillToLookup.District}
                                checked={timesheetEntryTasks.filter(x => x.taskTypeId === TaskTypeLookup.Other && x.billToId === BillToLookup.District).length > 0}
                                onChange={(e) => taskTypeChanged(TaskTypeLookup.Other, Number(e.target.value))}
                            /> {'Other Billed To District'}
                        </label>
                    </div>

                    {schoolIsEmpty &&
                        (
                            <div style={{ color: 'gray' }}>
                                <br />
                                <span className="mt-3 text-sm italic">
                                    Note: Clinical Supervision, MTSS, and Other Billed To School require a School. Please click Change at the top left of this box to add a School.
                                </span>
                            </div>
                        )
                    }

                    {/** Entries Container */}
                    <div className={`entries-container overflow-x-hidden drop-shadow-lg ${timesheetEntryTasks.length > 0 ? 'max-h-96 block' : 'hidden'} ${timesheetEntryTasks.length >= 4 ? 'overflow-scroll scrollbar' : 'overflow-none'} mt-8`}>
                        {/* If a student and tasktype is selected, display the week entries */}
                        {(timesheetEntryTasks?.length ?? 0) > 0 &&
                            <div className="">
                                {timesheetEntryTasks
                                    .filter(x => x.taskTypeId === TaskTypeLookup.Other ? x.taskTypeId === TaskTypeLookup.Other && x.billToId : true)
                                    //?.sort((a, b) => a.taskType.name.localeCompare(b.taskType.name))
                                    //?.sort((a, b) => taskTypes.find(x => x.taskTypeId === a.taskTypeId)?.hasRecoupment ? -1 : 1)
                                    ?.map((entryTask, i) => {
                                        if (!entryTask.entries) entryTask.entries = [];
                                        if (entryTask.entries.length === 0) {
                                            const tempEntries: DayEntry[] = [];

                                            if (!entryTask.taskType) {
                                                entryTask.taskType = taskTypes.find(x => x.taskTypeId === entryTask.taskTypeId);
                                            }

                                            daysOfWeek(entryState.startDate).forEach((day) => {
                                                tempEntries.push({
                                                    dateOfEntry: day,
                                                    notes: "",
                                                    minutes: entryTask.taskTypeId === TaskTypeLookup.Absence || entryTask.taskTypeId === TaskTypeLookup.Unavailable ? undefined : 0,
                                                    enabled: true,
                                                });
                                            });
                                            entryTask.entries = tempEntries;
                                        }

                                        //Get the task type
                                        const tt = taskTypes.find(x => x.taskTypeId === entryTask.taskTypeId);

                                        //Get the billing type name
                                        const billToName = entryTask.taskTypeId === TaskTypeLookup.Other ? getBillingTypeName(entryTask.billToId ?? 0) : '';

                                        return (
                                            <div className="entryTaskContainer">
                                                {/** Task Type Name */}
                                                <h3 className="block">{`${tt?.name}${billToName && billToName !== '' ? ' - ' + billToName : ''}`}</h3>
                                                <div className="flex flex-row w-full mt-2 justify-evenly">
                                                    <div
                                                        className={`flex flex-row w-full mt-2 justify-evenly entry-option-${entryTask.taskTypeId}${entryTask.billToId ? '-' + entryTask.billToId : ''}`}
                                                        key={`entry-option-${entryTask.taskTypeId}${entryTask.billToId ? '-' + entryTask.billToId : ''}`}>
                                                        {entryTask.entries.map((entry, i) => {
                                                            const dateOfEntry = moment(entry.dateOfEntry ?? entry.date);
                                                            const isWeekend = dateOfEntry.isoWeekday() === 6 || dateOfEntry.isoWeekday() === 7;
                                                            if (isWeekend && !entryState.district?.enableWeekendBilling) return null;
                                                            const payrateExists = selectedDistrictProgramOption?.payrates!.some(pr => {
                                                                if (!pr.payrateStartDate) return false;
                                                                const start = moment(pr.payrateStartDate);
                                                                const end = pr.payrateEndDate ? moment(pr.payrateEndDate) : moment();
                                                                if (!start.isValid() || !end.isValid()) return false;
                                                                return dateOfEntry.isBetween(start, end, undefined, '[]');
                                                            }) ?? false;
                                                            return (
                                                                <div className={`w-20 text-center entry-container-${entryTask.taskTypeId}${entryTask.billToId ? '-' + entryTask.billToId : ''}${entry.billToId ? '-' + entry.billToId : ''}${moment(dateOfEntry).format('YYYY-MM-DD')}`} key={`entry-container-${entryTask.taskTypeId}${entryTask.billToId ? '-' + entryTask.billToId : ''}${entry.billToId ? '-' + entry.billToId : ''}${moment(dateOfEntry).format('YYYY-MM-DD')}`}>
                                                                    {/** Date of entry */}
                                                                    <div className={`font-bold ${((entry.dateOfEntry >= entryState.startDate && entry.dateOfEntry <= entryState.endDate && payrateExists) == false) ? "text-gray-300" : ""}`}>
                                                                        {dateOfEntry.format('ddd')}
                                                                    </div>
                                                                    <div className={`text-xs mb-2 ${((entry.dateOfEntry >= entryState.startDate && entry.dateOfEntry <= entryState.endDate && payrateExists) == false) ? "text-gray-300" : ""}`}>
                                                                        {dateOfEntry.format('M/D')}
                                                                    </div>

                                                                    {/** Minutes Text Box */}
                                                                    <input className={`inline-block text-center rounded-md h-12 w-12 appearance-none border mb-1 ${(entry.enabled && ((entry.dateOfEntry >= entryState.startDate && entry.dateOfEntry <= entryState.endDate && payrateExists) == true) && !moment().isBefore(dateOfEntry)) ? "border-black" : "border-slate-100 bg-gray-100"}`}
                                                                        value={entry?.minutes ? entry.minutes.valueOf() : "" ?? ""}
                                                                        onChange={(e) => updateEntryTime(i, Number(e.target.value.replace(/\D/g, "")), entryTask.taskTypeId ?? 0, entryTask.billToId)}
                                                                        disabled={(!entry.enabled || moment().isBefore(dateOfEntry) || ((entry.dateOfEntry >= entryState.startDate && entry.dateOfEntry <= entryState.endDate && payrateExists) == false))} />

                                                                    {/** Add/Edit Note */}
                                                                    {(entry.minutes ?? 0) > 0 && ((entry.dateOfEntry >= entryState.startDate && entry.dateOfEntry <= entryState.endDate && payrateExists) == true) && (
                                                                        <div className="cursor-pointer text-ptsblue-dark" onClick={(e) => editNoteClicked(i, entryTask.taskTypeId, entryTask.billToId)}>{(entry.notes.length === 0) ? "Add Note" : "Edit Note"}</div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                            </div>
                        }
                    </div>
                </Fragment>
            )}

            {canEnterTime() === false && (
                <Fragment>
                    <div key={'dtna'} className="text-sm text-red-900">The selected disrict does not allow district time. Please select another district</div>
                </Fragment>
            )}

            {validationMsg != null && typeof validationMsg === "string" && (
                <div className="text-sm text-red-900">{validationMsg}</div>
            )}

            {validationMsg != null && typeof validationMsg !== "string" && validationMsg}
        </Fragment>
    );
}

export default EntryForNonStudent;